import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Clock, User, ArrowRight, Tag } from 'lucide-react';

const Blog = () => {
  const featuredPost = {
    id: 1,
    title: 'The Future of Lead Management: AI-Powered Insights for 2026',
    excerpt: 'Discover how artificial intelligence is transforming lead management and what it means for your business growth strategy in the coming year.',
    author: 'Sarah Johnson',
    role: 'Head of Product',
    date: 'Jan 8, 2026',
    readTime: '8 min read',
    category: 'Industry Insights',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=400&fit=crop',
  };

  const blogPosts = [
    {
      id: 2,
      title: '5 Lead Validation Mistakes That Are Costing You Money',
      excerpt: 'Learn about the most common lead validation errors and how to avoid them to maximize your ROI.',
      author: 'Michael Chen',
      role: 'Sales Director',
      date: 'Jan 5, 2026',
      readTime: '6 min read',
      category: 'Best Practices',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=250&fit=crop',
    },
    {
      id: 3,
      title: 'How to Integrate AALIGN with Your Existing CRM',
      excerpt: 'A step-by-step guide to seamlessly connecting AALIGN with HubSpot, Salesforce, and other popular CRMs.',
      author: 'Emily Rodriguez',
      role: 'Integration Specialist',
      date: 'Jan 2, 2026',
      readTime: '10 min read',
      category: 'Tutorials',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=250&fit=crop',
    },
    {
      id: 4,
      title: 'Case Study: How TechCorp Increased Conversions by 340%',
      excerpt: 'See how one of our customers transformed their lead management process and achieved remarkable results.',
      author: 'David Park',
      role: 'Customer Success',
      date: 'Dec 28, 2025',
      readTime: '5 min read',
      category: 'Case Studies',
      image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=400&h=250&fit=crop',
    },
    {
      id: 5,
      title: 'Understanding Lead Scoring: A Complete Guide',
      excerpt: 'Everything you need to know about implementing an effective lead scoring system for your sales team.',
      author: 'Sarah Johnson',
      role: 'Head of Product',
      date: 'Dec 22, 2025',
      readTime: '12 min read',
      category: 'Best Practices',
      image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=400&h=250&fit=crop',
    },
    {
      id: 6,
      title: 'The ROI of Automated Lead Validation',
      excerpt: 'Calculate the true return on investment when you automate your lead validation process.',
      author: 'Michael Chen',
      role: 'Sales Director',
      date: 'Dec 18, 2025',
      readTime: '7 min read',
      category: 'Industry Insights',
      image: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=400&h=250&fit=crop',
    },
  ];

  const categories = ['All', 'Industry Insights', 'Best Practices', 'Tutorials', 'Case Studies'];

  const getCategoryColor = (category) => {
    const colors = {
      'Industry Insights': 'bg-blue-100 text-blue-700',
      'Best Practices': 'bg-green-100 text-green-700',
      'Tutorials': 'bg-purple-100 text-purple-700',
      'Case Studies': 'bg-orange-100 text-orange-700',
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-6 bg-gradient-to-br from-orange-50 via-white to-red-50">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl lg:text-5xl font-black mb-4">Blog</h1>
          <p className="text-xl text-gray-600 max-w-2xl">
            Insights, tutorials, and best practices to help you master lead management and grow your business.
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="py-6 px-6 bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-3 overflow-x-auto pb-2">
            {categories.map((category, idx) => (
              <button
                key={idx}
                className={`px-4 py-2 rounded-full font-medium whitespace-nowrap transition-colors ${
                  idx === 0 
                    ? 'bg-orange-500 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
            <div className="grid lg:grid-cols-2">
              <div className="h-64 lg:h-auto">
                <img 
                  src={featuredPost.image} 
                  alt={featuredPost.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getCategoryColor(featuredPost.category)}`}>
                    {featuredPost.category}
                  </span>
                  <span className="text-sm text-gray-500">Featured</span>
                </div>
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4 hover:text-orange-600 transition-colors cursor-pointer">
                  {featuredPost.title}
                </h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {featuredPost.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center text-white font-bold">
                      {featuredPost.author.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{featuredPost.author}</p>
                      <p className="text-xs text-gray-500">{featuredPost.date} · {featuredPost.readTime}</p>
                    </div>
                  </div>
                  <button className="flex items-center gap-2 text-orange-600 font-semibold hover:gap-3 transition-all">
                    Read More <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold mb-8">Latest Articles</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <article 
                key={post.id} 
                className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group"
              >
                <div className="h-48 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getCategoryColor(post.category)}`}>
                      {post.category}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center text-white font-bold text-xs">
                        {post.author.charAt(0)}
                      </div>
                      <span className="text-sm text-gray-600">{post.author}</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Clock className="w-3 h-3" />
                      {post.readTime}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Load More */}
          <div className="text-center mt-12">
            <button className="px-8 py-3 border-2 border-orange-500 text-orange-600 font-semibold rounded-xl hover:bg-orange-50 transition-colors">
              Load More Articles
            </button>
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 px-6 bg-gradient-to-r from-orange-500 to-red-600">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
          <p className="text-orange-100 mb-8">
            Get the latest insights on lead management delivered straight to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <input 
              type="email" 
              placeholder="Enter your email"
              className="px-6 py-3 rounded-xl text-gray-900 w-full sm:w-80 focus:outline-none focus:ring-2 focus:ring-white"
            />
            <button className="px-8 py-3 bg-white text-orange-600 font-bold rounded-xl hover:bg-gray-100 transition-colors">
              Subscribe
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
